/**
 * A club's screen plus the member's own layer, merged into one panel.
 *
 *     base     the club's, read-only, fetched fresh every load
 *     overlay  the member's, per (user, screen)
 *     ───────────────────────────────────────────────────────
 *     merged   what the grid draws
 *
 * **The overlay holds additions and view state, never edits to the base.** That
 * is what makes "the club's half is read-only" enforceable rather than a
 * convention: there is nowhere to put a changed header or a changed expression,
 * so no surface can offer one. See `docs/features/entitlements.md`.
 *
 * ## The split, to the property
 *
 * | the club's — fixed | the member's — theirs |
 * |---|---|
 * | which columns exist, `header`, `type`, `notation`, `precision`, `format`, `colorBy`, `computed` | `width`, `pinned`, `hide`, and the order |
 * | the base condition | a condition of their own, **ANDed** with it |
 * | the default sort | their own sort, which replaces it |
 *
 * **A member narrows; they cannot widen.** The conditions are `all(base,
 * theirs)`, so a club that publishes `price > 10` has published a floor. The
 * alternative lets a member turn the club's screen into a different one while
 * still calling it by the club's name.
 *
 * **Hiding a base column is view state, not a fork.** The column comes back when
 * it is unhidden, and the club still decides whether it exists at all.
 */

import { useEffect, useRef } from "react";
import type { ScreenerConfig } from "@qufolio/model";
import { libraryForAnotherScreen, stripLinks } from "@qufolio/screener-grid";

type Column = NonNullable<ScreenerConfig["columns"]>[number];
type Sort = NonNullable<ScreenerConfig["sort"]>;
type Condition = ScreenerConfig["filter"];
type Library = NonNullable<ScreenerConfig["savedFilters"]>;

/** What a member may say about one of the club's columns. */
export interface ColumnView {
  width?: number;
  pinned?: Column["pinned"];
  hide?: boolean;
  /** Where it sits among the merged columns. Absent keeps the club's order. */
  order?: number;
}

export interface Overlay {
  /** Columns the member added. */
  columns: Column[];
  /** The member's own condition, ANDed with the base's. */
  filter?: Condition;
  /** Per-field view state over the base's columns. */
  columnView: Record<string, ColumnView>;
  /** The member's sort, which replaces the base's when it has one. */
  sort: Sort;
  /**
   * Conditions the member added beside the group's: **their own**, whole, and
   * ones **linked** in from another screen they can read
   * (`docs/plans/screen-links.md`) as `{id, name, link}`. Own conditions are
   * kept since 2026-10-30 (owner) — refusing them while the editor offered
   * them lost a member's condition on Impulse at the next load. Absent is
   * none, so a layer written before either reads unchanged.
   */
  savedFilters?: Library;
}

export const EMPTY_OVERLAY: Overlay = { columns: [], columnView: {}, sort: [] };

export interface ScreenBase {
  columns: Column[];
  filter?: Condition;
  sort: Sort;
  /** The panel's library — read by `splitOverlay` for the linked conditions in it. */
  savedFilters?: ScreenerConfig["savedFilters"];
}

/**
 * The library a member sees on a group's screen: the group's, then the
 * conditions they linked in. A link whose id the group's library already holds
 * is left out — one id is one tick.
 */
export function mergeLibrary(base: Library, overlay: Overlay): Library {
  const linked = overlay.savedFilters ?? [];
  if (linked.length === 0) return base;
  const ids = new Set(base.map((f) => f.id));
  return [...base, ...linked.filter((f) => !ids.has(f.id))];
}

/**
 * The member's condition and the club's, as one.
 *
 * `all` rather than a rewrite of either side, so the two stay separable: the
 * editor shows the club's as a preamble it cannot touch, and the member's as the
 * thing they are editing. Flattening them into a single tree would make the
 * member's half unrecoverable, which is how a linked screen turns into a copy
 * nobody meant to take.
 *
 * Either side missing is the other side alone — an empty `all` node would be a
 * condition that says nothing, and the grid would have to know to ignore it.
 */
export function mergeConditions(base: Condition, mine: Condition): Condition {
  if (!base) return mine;
  if (!mine) return base;
  return { op: "all", children: [base, mine] } as Condition;
}

/**
 * The panel a member sees: the club's columns with their view applied, then
 * their own.
 *
 * **Added columns come after the base's and cannot be interleaved by accident.**
 * A member who reorders puts an explicit `order` on the entry; without one the
 * club's order stands, so a club inserting a column does not shuffle a member's
 * screen around it.
 */
export function mergeScreen(base: ScreenBase, overlay: Overlay): ScreenBase {
  const view = overlay.columnView ?? {};
  const dressed = base.columns.map((column) => {
    const mine = view[column.field ?? ""] ?? {};
    return {
      ...column,
      ...(mine.width !== undefined ? { width: mine.width } : {}),
      ...(mine.pinned !== undefined ? { pinned: mine.pinned } : {}),
      ...(mine.hide !== undefined ? { hide: mine.hide } : {}),
    };
  });

  return {
    columns: placeColumns([...dressed, ...overlay.columns], view),
    filter: mergeConditions(base.filter, overlay.filter),
    sort: overlay.sort.length > 0 ? overlay.sort : base.sort,
  };
}

/**
 * Put the columns the member moved where they put them, and let the rest flow.
 *
 * **A splice, not a sort key**, and the difference is the whole of it. Sorting
 * by "explicit order, else your index" ties an explicit `order: 0` against the
 * index of whatever already sat first, and the tie-break decides — so dragging a
 * column to the front did nothing. An explicit position means *this index in the
 * finished list*; everything else keeps its relative order and fills the gaps
 * around it, which is what a drag means to the person doing it.
 *
 * Placed columns are inserted in ascending order so two of them cannot contend
 * for one slot: the earlier index is filled first and the later one lands after
 * it, which is stable across renders.
 */
function placeColumns(
  columns: readonly Column[],
  view: Record<string, ColumnView>,
): Column[] {
  const placed: { column: Column; at: number }[] = [];
  const floating: Column[] = [];
  for (const column of columns) {
    const at = view[column.field ?? ""]?.order;
    if (at === undefined) floating.push(column);
    else placed.push({ column, at });
  }
  const out = [...floating];
  for (const { column, at } of [...placed].sort((a, b) => a.at - b.at)) {
    out.splice(Math.min(Math.max(at, 0), out.length), 0, column);
  }
  return out;
}

/**
 * Read a member's layer back out of the panel they have been editing.
 *
 * **This is what lets every existing editor stay as it is.** The column picker,
 * the width drag and the sort all patch the section, exactly as they do for a
 * screen you own; this diffs the result against the club's base and keeps only
 * the difference. The alternative was teaching five editors which half of a
 * merged screen they were allowed to touch.
 *
 * The condition is deliberately **not** recovered here. `mergeConditions`
 * produces `all(base, mine)` and picking `mine` back out of an arbitrary tree is
 * guesswork the moment somebody edits the merged form — so the member's
 * condition is carried through untouched and edited as its own thing.
 */
export function splitOverlay(
  base: ScreenBase,
  panel: ScreenBase,
  previous: Overlay = EMPTY_OVERLAY,
): Overlay {
  const baseFields = new Set(base.columns.map((c) => c.field));
  const columnView: Record<string, ColumnView> = {};
  const added: Column[] = [];

  panel.columns.forEach((column, index) => {
    const field = column.field ?? "";
    if (!baseFields.has(field)) {
      added.push(column);
      if (index !== base.columns.length + added.length - 1) {
        columnView[field] = { ...columnView[field], order: index };
      }
      return;
    }
    const original = base.columns.find((c) => c.field === field)!;
    const mine: ColumnView = {};
    if (column.width !== original.width) mine.width = column.width;
    if (column.pinned !== original.pinned) mine.pinned = column.pinned;
    if (column.hide !== original.hide) mine.hide = column.hide;
    if (index !== base.columns.indexOf(original)) mine.order = index;
    if (Object.keys(mine).length > 0) columnView[field] = mine;
  });

  // **A base column the panel no longer lists is hidden, not dropped.** The
  // club decides whether a column exists; a member only decides whether they
  // are looking at it, and a removal here would be a fork of the club's screen
  // that nothing on screen ever said had happened.
  for (const column of base.columns) {
    const field = column.field ?? "";
    if (!panel.columns.some((c) => c.field === field)) {
      columnView[field] = { ...columnView[field], hide: true };
    }
  }

  // The conditions the member added: every entry the group's library does not
  // hold by id — their own whole, a link as its link, a sealed one never (it
  // names a rule only its own screen has). Only when there are any, so a layer
  // with none encodes exactly as it did before.
  const baseIds = new Set((base.savedFilters ?? []).map((f) => f.id));
  const mine = libraryForAnotherScreen((panel.savedFilters ?? []).filter((f) => !baseIds.has(f.id)));

  return {
    // A linked column the member added is kept as its link and their view.
    columns: stripLinks(added),
    filter: previous.filter,
    columnView,
    sort: panel.sort,
    ...(mine.length > 0 ? { savedFilters: mine } : {}),
  };
}

/**
 * The panel a stored layer draws: `mergeScreen` plus the library.
 *
 * Used as the sync's baseline — the layer the server holds, encoded through
 * `splitOverlay` exactly as an edited panel is, so the two compare like for
 * like and a panel that already says what the server holds writes nothing.
 */
export function panelOf(base: ScreenBase, overlay: Overlay): ScreenBase {
  return { ...mergeScreen(base, overlay), savedFilters: mergeLibrary(base.savedFilters ?? [], overlay) };
}

/**
 * Push a member's layer back to the server after the panel settles.
 *
 * `useScreenSync`'s shape and its three guards, aimed at the other half of the
 * problem: that one writes the *screen* when you own it, this one writes the
 * *layer* when you do not. A screen can never take both — ownership decides —
 * which is what stops an edit to a club's screen being written twice or, worse,
 * once to the wrong place.
 *
 * The baseline is the encoded layer rather than the panel, because two different
 * panels can produce the same layer: a member who drags a column back where it
 * started has changed the panel twice and the layer not at all.
 *
 * **The baseline is the layer the server holds, not the first panel seen**
 * (2026-10-30). Taking the first panel meant a panel that already differed —
 * edits made while no layer was being written — was adopted as saved and never
 * sent: hide Name, reload, pick the tab again, and Name was back.
 *
 * **Nothing is written while the panel shows another screen.** Switching fills
 * the panel before the new base arrives, and diffing screen B's columns against
 * screen A's base would hide every one of A's columns in A's layer.
 */
export function useOverlaySync(input: {
  /** The screen being shown and the base it was filled from, or null. */
  linked: { key: string; base: ScreenBase; overlay: Overlay } | null;
  /** The panel as it stands. */
  panel: ScreenBase;
  /** The screen the panel is showing — its `screenRef`. */
  screenRef: string | undefined;
  save: (key: string, overlay: Overlay) => Promise<unknown>;
  onSaved?: (key: string, overlay: Overlay) => void;
  delay?: number;
}): void {
  const { linked, panel, screenRef, save, onSaved, delay = 600 } = input;
  const lastSaved = useRef(new Map<string, string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const key = linked && linked.key === screenRef ? linked.key : undefined;
  const next = key ? splitOverlay(linked!.base, panel, linked!.overlay) : null;
  const encoded = next ? JSON.stringify(next) : "";
  const stored = key
    ? JSON.stringify(splitOverlay(linked!.base, panelOf(linked!.base, linked!.overlay), linked!.overlay))
    : "";

  useEffect(() => {
    if (!key || !next) return;
    const seen = lastSaved.current;
    if (!seen.has(key)) seen.set(key, stored);
    if (seen.get(key) === encoded) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      seen.set(key, encoded);
      const layer = JSON.parse(encoded) as Overlay;
      void save(key, layer)
        .then(() => onSaved?.(key, layer))
        // Unknown rather than deleted, for `useScreenSync`'s reason: deleting
        // makes the next edit look like first sight, which is the one case that
        // deliberately does not save.
        .catch(() => seen.set(key, ""));
    }, delay);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [key, encoded, stored, delay, save, onSaved, next]);
}
